<template>
  <div class="call-history-wrapper">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Call History
        </h1>
        <div class="page-actions">
          <v-btn
            variant="outlined"
            color="secondary"
            class="mank-btn refresh-btn"
            :disabled="loading || loadingMore || loadingDetails"
            @click="refreshCalls">
            <Icon
              name="reset"
              variant="solid"
              color="secondary"
              size="16"
              :class="['refresh-btn-icon', { 'refresh-btn-icon--spinning': loading || loadingDetails }]"/>
            Refresh
          </v-btn>
          <div class="filter-btn-wrap">
            <v-btn
              :variant="hasActiveFilters ? 'flat' : 'outlined'"
              color="secondary"
              class="mank-btn filter-btn"
              @click="openFilterDrawer">
              <Icon
                name="filter"
                variant="solid"
                :color="hasActiveFilters ? 'onPrimary' : 'secondary'"
                size="16"
                class="filter-btn-icon"/>
              Filter
            </v-btn>
            <span
              v-if="hasActiveFilters"
              class="filter-active-indicator">
              <Icon name="check-circle" variant="solid" color="success" size="14"/>
            </span>
          </div>
          <v-btn
            variant="outlined"
            color="primary"
            class="mank-btn export-btn"
            :loading="exporting"
            :disabled="loading || exporting"
            @click="exportCalls">
            {{ exporting ? 'Exporting...' : 'Export Data' }}
          </v-btn>
        </div>
      </div>

      <div class="main-content">
        <!-- İstatistikler -->
        <div class="stats-grid">
          <div class="stat-card">
            <h4 class="stat-label">
              Total Calls
            </h4>
            <p class="stat-value">
              {{ totalCallsDisplay }}
            </p>
          </div>
        </div>

        <!-- Tablo -->
        <div
          ref="tableScrollArea"
          class="table-area table-area--scroll"
          @scroll.passive="onTableScroll">
          <div v-if="loading" class="loading-state">
            Loading records...
          </div>
          <div v-else-if="calls.length === 0" class="empty-state-table">
            No calls found.
          </div>
          <div v-else class="table-container">
            <table class="call-history-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>TEL NO</th>
                  <th>Customer Number</th>
                  <th>Started At</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="call in calls">
                  <tr
                    :key="`row-${call.id}`"
                    :class="['data-row', { 'row-expanded': expandedId === call.id }]"
                    @click.stop>
                    <td class="font-medium">
                      {{ call.agent_id }}
                    </td>
                    <td class="customer-id-cell" :title="displayTelNo(call)">
                      {{ displayTelNo(call) }}
                    </td>
                    <td class="customer-number-cell" :title="displayCustomerNumber(call)">
                      {{ displayCustomerNumber(call) }}
                    </td>
                    <td>{{ formatDateTime(call.started_at) }}</td>
                    <td>{{ formatDuration(call.duration_seconds) }}</td>
                    <td>
                      <span
                        :class="['status-badge', `status-${getStatusClass(call.status)}`]">
                        {{ call.status }}
                      </span>
                    </td>
                    <td>
                      <v-btn
                        :variant="expandedId === call.id ? 'outlined' : 'flat'"
                        color="primary"
                        size="small"
                        @click.stop="toggleRow(call.id)">
                        {{ expandedId === call.id ? 'Close' : 'Details' }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr
                    v-if="expandedId === call.id"
                    :key="`detail-${call.id}`"
                    class="detail-row">
                    <td colspan="7" class="detail-cell">
                      <div class="detail-inner">
                        <div class="audio-block">
                          <div class="audio-header">
                            <span class="audio-label">Conversation Recording</span>
                            <span
                              v-if="expandedCallData && expandedCallData.sentiment"
                              class="sentiment-badge">
                              {{ expandedCallData.sentiment }} Sentiment
                            </span>
                          </div>
                          <div class="call-meta-lines">
                            <p class="call-meta-line">
                              <span class="call-meta-label">Summary:</span>
                              <span class="call-meta-value">
                                {{ expandedRowSummary(call) || '—' }}
                              </span>
                            </p>
                            <p class="call-meta-line">
                              <span class="call-meta-label">Call ID:</span>
                              <span class="call-meta-value call-meta-mono">
                                {{ expandedCallId(call) }}
                              </span>
                            </p>
                            <p class="call-meta-line">
                              <span class="call-meta-label">Conn ID:</span>
                              <span class="call-meta-value call-meta-mono">
                                {{ expandedConnId(call) }}
                              </span>
                            </p>
                            <p class="call-meta-line">
                              <span class="call-meta-label">Result Code:</span>
                              <span class="call-meta-value call-meta-mono">
                                {{ expandedResultCode(call) }}
                              </span>
                            </p>
                            <p class="call-meta-line">
                              <span class="call-meta-label">MT Transferred:</span>
                              <span class="call-meta-value call-meta-mono">
                                {{ expandedMtTransferred(call) }}
                              </span>
                            </p>
                          </div>
                          <div
                            v-if="loadingDetails || !expandedCallData"
                            class="detail-body-loading">
                            Loading details…
                          </div>
                          <template v-else>
                            <p
                              v-if="callRecordingMissing"
                              class="no-audio">
                              No audio recording available.
                            </p>
                            <audio
                              v-else-if="callRecordingUrl"
                              ref="audioEl"
                              :src="callRecordingUrl"
                              controls
                              class="audio-element"
                              @play="isPlaying = true"
                              @pause="isPlaying = false"
                              @timeupdate="onTimeUpdate"/>
                            <v-btn
                              v-else-if="!callRecordingLoading"
                              text
                              small
                              color="primary"
                              class="call-recording-load"
                              @click="loadCallRecording">
                              <v-icon left small>
                                mdi-play-circle-outline
                              </v-icon>
                              Conversation audio
                            </v-btn>
                            <span
                              v-else
                              class="call-recording-loading">
                              Loading…
                            </span>
                          </template>
                        </div>

                        <div
                          v-if="hasRenderableMetadata(call)"
                          class="metadata-block">
                          <div
                            class="metadata-expand-header"
                            role="button"
                            tabindex="0"
                            :aria-expanded="callMetadataSectionOpen ? 'true' : 'false'"
                            @click="toggleMetadataSection"
                            @keydown.enter.prevent="toggleMetadataSection"
                            @keydown.space.prevent="toggleMetadataSection">
                            <span class="metadata-expand-title">Metadata</span>
                            <v-icon
                              size="22"
                              color="icon-muted"
                              class="metadata-chevron">
                              {{ callMetadataSectionOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                          </div>
                          <v-expand-transition>
                            <div
                              v-show="callMetadataSectionOpen"
                              class="metadata-expand-body">
                              <pre class="metadata-pre">{{ metadataPreformatted(call) }}</pre>
                            </div>
                          </v-expand-transition>
                        </div>

                        <div
                          v-if="!loadingDetails && expandedCallData"
                          class="detail-content">
                          <!-- Transcript -->
                          <div class="transcript-block">
                            <h4 class="transcript-title">
                              Transcript Analysis
                            </h4>
                            <div class="transcript-list">
                              <div
                                v-for="(segment, idx) in (expandedCallData.transcript || [])"
                                :key="idx"
                                :class="[
                                  'transcript-segment',
                                  {
                                    'segment-active': isSegmentActive(segment),
                                    'segment-agent': segment.role === 'agent',
                                  },
                                ]"
                                @click="jumpToTime(segment.timestamp_start)">
                                <div
                                  :class="[
                                    'segment-dot',
                                    {
                                      'dot-active': isSegmentActive(segment),
                                      'dot-agent': segment.role === 'agent',
                                    },
                                  ]"/>
                                <div
                                  :class="[
                                    'segment-bubble',
                                    { 'bubble-active': isSegmentActive(segment) },
                                  ]">
                                  <div class="segment-meta">
                                    <div class="segment-meta-left">
                                      <span class="segment-speaker">
                                        {{ segment.speaker_name || segment.role }}
                                      </span>
                                      <span
                                        v-if="segment.stage"
                                        class="stage-badge">
                                        {{ segment.stage }}
                                      </span>
                                      <span
                                        v-if="segment.confidence != null"
                                        class="confidence-badge">
                                        {{ segment.confidence }}%
                                      </span>
                                    </div>
                                    <span class="segment-time">
                                      {{ formatDuration(segment.timestamp_start) }}
                                    </span>
                                  </div>
                                  <p class="segment-text">
                                    {{ segment.content }}
                                  </p>
                                  <div
                                    class="segment-metrics"
                                    @click.stop>
                                    <button
                                      class="segment-metrics-toggle"
                                      @click.stop="toggleSegmentMetrics(idx)">
                                      <v-icon size="13" class="segment-metrics-chevron">
                                        {{ expandedSegments[idx] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                      </v-icon>
                                      {{ expandedSegments[idx] ? 'Hide metrics' : 'Show metrics' }}
                                    </button>
                                    <v-expand-transition>
                                      <div v-show="expandedSegments[idx]" class="segment-metrics-body">
                                        <div class="metrics-grid">
                                          <div class="metric-item">
                                            <p class="metric-label">Stage Transition</p>
                                            <p class="metric-value">{{ segment.is_stage_transition ? 'true' : 'false' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">STT Latency</p>
                                            <p class="metric-value">{{ segment.stt_latency != null ? segment.stt_latency + ' ms' : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">TTFT Latency</p>
                                            <p class="metric-value">{{ segment.ttft_latency != null ? segment.ttft_latency + ' ms' : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">ITL Latency</p>
                                            <p class="metric-value">{{ segment.itl_latency != null ? segment.itl_latency + ' ms' : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">TTS Latency</p>
                                            <p class="metric-value">{{ segment.tts_latency != null ? segment.tts_latency + ' ms' : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">TTS RTF</p>
                                            <p class="metric-value">{{ segment.tts_rtf != null ? (segment.tts_rtf * 100).toFixed(2) + '%' : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">Token/s</p>
                                            <p class="metric-value">{{ segment.token_second != null ? Number(segment.token_second).toFixed(2) : '—' }}</p>
                                          </div>
                                          <div class="metric-item">
                                            <p class="metric-label">Total Tokens</p>
                                            <p class="metric-value">{{ segment.total_token != null ? segment.total_token : '—' }}</p>
                                          </div>
                                        </div>
                                        <div
                                          v-if="hasRenderableValue(segment.tool_calls)"
                                          class="segment-tool-block">
                                          <p class="metric-label">Tool Calls</p>
                                          <pre class="metadata-pre">{{ formatJsonValue(segment.tool_calls) }}</pre>
                                        </div>
                                        <div
                                          v-if="hasRenderableValue(segment.tool_names)"
                                          class="segment-tool-block">
                                          <p class="metric-label">Tool Names</p>
                                          <pre class="metadata-pre">{{ formatJsonValue(segment.tool_names) }}</pre>
                                        </div>
                                      </div>
                                    </v-expand-transition>
                                  </div>
                                  <div
                                    v-if="segment.id"
                                    class="message-audio-row"
                                    @click.stop>
                                    <p
                                      v-if="messageAudioMissing[segment.id]"
                                      class="message-audio-none">
                                      No audio for this message.
                                    </p>
                                    <audio
                                      v-else-if="messageAudioUrls[segment.id]"
                                      :src="messageAudioUrls[segment.id]"
                                      controls
                                      class="message-audio-el"/>
                                    <v-btn
                                      v-else-if="!messageAudioLoading[segment.id]"
                                      text
                                      small
                                      color="primary"
                                      class="message-audio-load"
                                      @click.stop="loadMessageAudio(segment.id)">
                                      <v-icon left small>
                                        mdi-play-circle-outline
                                      </v-icon>
                                      Message audio
                                    </v-btn>
                                    <span
                                      v-else
                                      class="message-audio-loading">
                                      Loading…
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <div v-if="loadingMore" class="scroll-pagination-hint">
              Loading more…
            </div>
            <div
              v-else-if="!hasMore && calls.length > 0"
              class="scroll-pagination-hint scroll-pagination-hint--end">
              End of list
            </div>
            <div
              v-else-if="hasMore && !tableScrollRootOverflows && !loadingMore && calls.length > 0"
              class="scroll-pagination-hint">
              <v-btn
                variant="text"
                color="primary"
                class="load-more-btn"
                @click="loadMoreCalls">
                Load more
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-navigation-drawer
      v-model="filterDrawerOpen"
      right
      fixed
      temporary
      width="420"
      class="call-filter-drawer">
      <div class="filter-drawer-header">
        <h3 class="filter-drawer-title">
          Filters
        </h3>
        <v-btn icon variant="text" class="filter-drawer-close" @click="closeFilterDrawer">
          <Icon name="cross" variant="solid" size="16" color="default"/>
        </v-btn>
      </div>

      <div class="filter-drawer-body">
        <div class="filter-field filter-field--date">
          <v-text-field
            v-model="draftFilters.startDateTime"
            label="Start Date & Time"
            type="datetime-local"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field"/>
        </div>

        <div class="filter-field filter-field--date">
          <v-text-field
            v-model="draftFilters.endDateTime"
            label="End Date & Time"
            type="datetime-local"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field"/>
        </div>

        <div class="filter-field">
          <v-text-field
            v-model="draftFilters.callId"
            placeholder="Call ID — exact match"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field"/>
        </div>
        <div class="filter-field">
          <v-text-field
            v-model="draftFilters.connId"
            placeholder="Conn ID — exact match"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field"/>
        </div>

        <div class="filter-field">
          <v-text-field
            v-model="draftFilters.agentId"
            placeholder="Agent ID (min. 3 characters)…"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field">
            <template v-slot:append-inner>
              <v-icon size="20" color="icon-muted">
                mdi-magnify
              </v-icon>
            </template>
          </v-text-field>
          <p v-if="showDraftAgentIdMinLengthHint" class="filter-search-hint">
            Enter at least 3 characters to search.
          </p>
        </div>

        <div class="filter-field">
          <v-text-field
            v-model="draftFilters.customerId"
            placeholder="TEL NO — partial match (min. 3 characters)…"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field">
            <template v-slot:append-inner>
              <v-icon size="20" color="icon-muted">
                mdi-magnify
              </v-icon>
            </template>
          </v-text-field>
          <p v-if="showDraftCustomerIdMinLengthHint" class="filter-search-hint">
            Enter at least 3 characters to search.
          </p>
        </div>

        <div class="filter-field">
          <v-text-field
            v-model="draftFilters.customerNumber"
            placeholder="Customer Number — partial match (min. 3 characters)…"
            variant="outlined"
            density="compact"
            dense
            hide-details
            class="search-input-field">
            <template v-slot:append-inner>
              <v-icon size="20" color="icon-muted">
                mdi-magnify
              </v-icon>
            </template>
          </v-text-field>
          <p v-if="showDraftCustomerNumberMinLengthHint" class="filter-search-hint">
            Enter at least 3 characters to search.
          </p>
        </div>
      </div>

      <div class="filter-drawer-footer">
        <v-btn variant="outlined" color="default" class="mank-btn" @click="resetFilters">
          Reset
        </v-btn>
        <v-btn variant="flat" color="primary" class="mank-btn" @click="applyFilters">
          Apply Filters
        </v-btn>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import callHistoryService from '@/services/CallHistoryService';
import callMessageService from '@/services/CallMessageService';
import AnalyticsService from '@/services/AnalyticsService';
import { EVENTS } from '@/constants/analyticsEvents';
import Icon from '@/components/Icon.vue';

const CALLS_PAGE_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 140;
const MIN_LIKE_FILTER_LEN = 3;

export default {
  name: 'CallHistory',
  components: { Icon },
  data() {
    return {
      calls: [],
      loading: true,
      loadingMore: false,
      pagination: null,
      pageSize: CALLS_PAGE_SIZE,
      filterDrawerOpen: false,
      startDateTimeFilter: '',
      endDateTimeFilter: '',
      callIdFilter: '',
      connIdFilter: '',
      agentIdFilter: '',
      customerIdFilter: '',
      customerNumberFilter: '',
      draftFilters: {
        startDateTime: '',
        endDateTime: '',
        callId: '',
        connId: '',
        agentId: '',
        customerId: '',
        customerNumber: '',
      },
      expandedId: null,
      expandedCallData: null,
      loadingDetails: false,
      isPlaying: false,
      currentTime: 0,
      messageAudioUrls: {},
      messageAudioLoading: {},
      messageAudioMissing: {},
      callRecordingUrl: null,
      callRecordingLoading: false,
      callRecordingMissing: false,
      exporting: false,
      /** True when .table-area scrollHeight > clientHeight (user can scroll). If false, IO would fire non-stop — use Load more instead. */
      tableScrollRootOverflows: false,
      callMetadataSectionOpen: false,
      expandedSegments: {},
      tableScrollResizeListener: null,
    };
  },
  computed: {
    hasMore() {
      const p = this.pagination;
      if (!p || p.paginationExhausted) return false;
      const totalPages = p.total_pages != null ? Number(p.total_pages) : null;
      const page = p.page != null ? Number(p.page) : null;
      if (totalPages != null && page != null && Number.isFinite(totalPages) && Number.isFinite(page)) {
        return page < totalPages;
      }
      return !!p.inferredMore;
    },
    totalCallsDisplay() {
      const t = this.pagination?.total;
      if (typeof t === 'number') return t;
      return this.calls.length;
    },
    callIdForApi() {
      const t = (this.callIdFilter || '').trim();
      if (!t) return undefined;
      return t;
    },
    connIdForApi() {
      const t = (this.connIdFilter || '').trim();
      if (!t) return undefined;
      return t;
    },
    agentIdForApi() {
      const t = (this.agentIdFilter || '').trim();
      if (t.length < MIN_LIKE_FILTER_LEN) return undefined;
      return t;
    },
    customerIdForApi() {
      const t = (this.customerIdFilter || '').trim();
      if (t.length < MIN_LIKE_FILTER_LEN) return undefined;
      return t;
    },
    showDraftAgentIdMinLengthHint() {
      const t = (this.draftFilters.agentId || '').trim();
      return t.length > 0 && t.length < MIN_LIKE_FILTER_LEN;
    },
    showDraftCustomerIdMinLengthHint() {
      const t = (this.draftFilters.customerId || '').trim();
      return t.length > 0 && t.length < MIN_LIKE_FILTER_LEN;
    },
    customerNumberForApi() {
      const t = (this.customerNumberFilter || '').trim();
      if (t.length < MIN_LIKE_FILTER_LEN) return undefined;
      return t;
    },
    showDraftCustomerNumberMinLengthHint() {
      const t = (this.draftFilters.customerNumber || '').trim();
      return t.length > 0 && t.length < MIN_LIKE_FILTER_LEN;
    },
    startDateForApi() {
      if (!this.startDateTimeFilter) return undefined;
      const d = new Date(this.startDateTimeFilter);
      const ts = Math.floor(d.getTime() / 1000);
      if (!Number.isFinite(ts) || ts <= 0) return undefined;
      return ts;
    },
    endDateForApi() {
      if (!this.endDateTimeFilter) return undefined;
      const d = new Date(this.endDateTimeFilter);
      const ts = Math.floor(d.getTime() / 1000);
      if (!Number.isFinite(ts) || ts <= 0) return undefined;
      return ts;
    },
    hasActiveFilters() {
      return !!(
        this.startDateForApi
        || this.endDateForApi
        || this.callIdForApi
        || this.connIdForApi
        || this.agentIdForApi
        || this.customerIdForApi
        || this.customerNumberForApi
      );
    },
  },
  beforeDestroy() {
    if (this.tableScrollResizeListener) {
      window.removeEventListener('resize', this.tableScrollResizeListener);
    }
    this.revokeAllMessageAudioUrls();
    this.resetCallRecordingState();
  },
  watch: {
    expandedId() {
      this.callMetadataSectionOpen = false;
      this.expandedSegments = {};
    },
  },
  mounted() {
    this.tableScrollResizeListener = () => this.updateTableScrollOverflowFlag();
    window.addEventListener('resize', this.tableScrollResizeListener);
    this.fetchCalls();
  },
  methods: {
    buildActiveFiltersForApi({ includePaging = false, page = 1 } = {}) {
      const payload = {
        start_date: this.startDateForApi,
        end_date: this.endDateForApi,
        call_id: this.callIdForApi,
        conn_id: this.connIdForApi,
        agent_id: this.agentIdForApi,
        customer_id: this.customerIdForApi,
        customer_number: this.customerNumberForApi,
      };
      if (includePaging) {
        payload.page = page;
        payload.page_size = this.pageSize;
      }
      return payload;
    },
    openFilterDrawer() {
      this.draftFilters = {
        startDateTime: this.startDateTimeFilter,
        endDateTime: this.endDateTimeFilter,
        callId: this.callIdFilter,
        connId: this.connIdFilter,
        agentId: this.agentIdFilter,
        customerId: this.customerIdFilter,
        customerNumber: this.customerNumberFilter,
      };
      this.filterDrawerOpen = true;
    },
    closeFilterDrawer() {
      this.filterDrawerOpen = false;
    },
    async applyFilters() {
      this.startDateTimeFilter = this.draftFilters.startDateTime;
      this.endDateTimeFilter = this.draftFilters.endDateTime;
      this.callIdFilter = this.draftFilters.callId;
      this.connIdFilter = this.draftFilters.connId;
      this.agentIdFilter = this.draftFilters.agentId;
      this.customerIdFilter = this.draftFilters.customerId;
      this.customerNumberFilter = this.draftFilters.customerNumber;
      this.filterDrawerOpen = false;

      const appliedFilters = Object.keys(this.draftFilters)
        .filter((key) => !!this.draftFilters[key]);
      AnalyticsService.track(EVENTS.CALL_FILTER_APPLIED, { filters: appliedFilters });

      await this.fetchCalls();
    },
    async resetFilters() {
      const cleared = {
        startDateTime: '',
        endDateTime: '',
        callId: '',
        connId: '',
        agentId: '',
        customerId: '',
        customerNumber: '',
      };
      this.draftFilters = { ...cleared };
      this.startDateTimeFilter = '';
      this.endDateTimeFilter = '';
      this.callIdFilter = '';
      this.connIdFilter = '';
      this.agentIdFilter = '';
      this.customerIdFilter = '';
      this.customerNumberFilter = '';
      this.filterDrawerOpen = false;
      await this.fetchCalls();
    },
    /** TEL NO — API `customer_id` (JSON); yedek: camelCase / nested. */
    displayTelNo(call) {
      if (!call) return '—';
      const raw = call.customer_id
        ?? call.customerId
        ?? call.CustomerID
        ?? (call.attributes && (call.attributes.customer_id ?? call.attributes.customerId))
        ?? (call.customer && (call.customer.id ?? call.customer.customer_id ?? call.customer.customerId));
      if (raw == null || String(raw).trim() === '') return '—';
      return String(raw).trim();
    },
    /** Customer Number — API `customer_number` (JSON); yedek: camelCase / nested. */
    displayCustomerNumber(call) {
      if (!call) return '—';
      const raw = call.customer_number
        ?? call.customerNumber
        ?? call.CustomerNumber
        ?? (call.attributes && (call.attributes.customer_number ?? call.attributes.customerNumber))
        ?? (call.customer && (call.customer.number ?? call.customer.customer_number ?? call.customer.customerNumber));
      if (raw == null || String(raw).trim() === '') return '—';
      return String(raw).trim();
    },
    updateTableScrollOverflowFlag() {
      this.$nextTick(() => {
        const el = this.$refs.tableScrollArea;
        if (!el) {
          this.tableScrollRootOverflows = false;
          return;
        }
        this.tableScrollRootOverflows = el.scrollHeight > el.clientHeight + 2;
      });
    },
    expandedRowSummary(call) {
      const fromDetail = this.expandedCallData?.summary;
      if (fromDetail != null && String(fromDetail).trim() !== '') {
        return String(fromDetail).trim();
      }
      if (call?.summary != null && String(call.summary).trim() !== '') {
        return String(call.summary).trim();
      }
      return '';
    },
    expandedCallId(call) {
      const fromDetail = this.expandedCallData?.id;
      if (fromDetail != null && String(fromDetail).trim() !== '') {
        return String(fromDetail).trim();
      }
      return call?.id != null ? String(call.id) : '—';
    },
    expandedResultCode(call) {
      const fromDetail = this.expandedCallData?.result_code ?? this.expandedCallData?.resultCode;
      if (fromDetail != null) return String(fromDetail);
      const fromRow = call?.result_code ?? call?.resultCode;
      if (fromRow != null) return String(fromRow);
      return '—';
    },
    expandedMtTransferred(call) {
      const fromDetail = this.expandedCallData?.mt_transferred ?? this.expandedCallData?.mtTransferred;
      if (fromDetail != null) return fromDetail ? 'true' : 'false';
      const fromRow = call?.mt_transferred ?? call?.mtTransferred;
      if (fromRow != null) return fromRow ? 'true' : 'false';
      return '—';
    },
    expandedConnId(call) {
      const fromDetail = this.expandedCallData?.conn_id
        ?? this.expandedCallData?.connId
        ?? this.expandedCallData?.connection_id
        ?? this.expandedCallData?.connectionId;
      if (fromDetail != null && String(fromDetail).trim() !== '') {
        return String(fromDetail).trim();
      }
      const fromRow = call?.conn_id
        ?? call?.connId
        ?? call?.connection_id
        ?? call?.connectionId;
      if (fromRow != null && String(fromRow).trim() !== '') {
        return String(fromRow).trim();
      }
      return '—';
    },
    toggleMetadataSection() {
      this.callMetadataSectionOpen = !this.callMetadataSectionOpen;
    },
    callMetadataRaw(call) {
      const fromDetail = this.expandedCallData?.metadata;
      if (fromDetail !== undefined && fromDetail !== null) {
        return fromDetail;
      }
      return call?.metadata !== undefined ? call.metadata : null;
    },
    hasRenderableValue(v) {
      if (v === null || v === undefined) return false;
      if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === 'string' && String(v).trim() === '') return false;
      return true;
    },
    formatJsonValue(v) {
      if (v == null) return '';
      try {
        if (typeof v === 'string') {
          const trimmed = v.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
              return JSON.stringify(JSON.parse(v), null, 2);
            } catch (e) {
              return v;
            }
          }
          return v;
        }
        return JSON.stringify(v, null, 2);
      } catch (e) {
        return String(v);
      }
    },
    hasRenderableMetadata(call) {
      return this.hasRenderableValue(this.callMetadataRaw(call));
    },
    metadataPreformatted(call) {
      return this.formatJsonValue(this.callMetadataRaw(call));
    },
    onTableScroll(e) {
      const el = e.target;
      if (!el || this.loading || this.loadingMore || !this.hasMore) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining <= SCROLL_LOAD_THRESHOLD_PX) {
        this.loadMoreCalls();
      }
    },
    async fetchCalls() {
      try {
        this.loading = true;
        this.pagination = null;
        const response = await callHistoryService.getListPage(
          this.buildActiveFiltersForApi({ includePaging: true, page: 1 }),
        );
        if (response.success) {
          this.calls = response.items;
          const p = { ...response.pagination };
          if (response.items.length < this.pageSize) p.paginationExhausted = true;
          if (p.total_pages != null && p.page != null
            && Number(p.page) >= Number(p.total_pages)) p.paginationExhausted = true;
          this.pagination = p;
        } else {
          this.calls = [];
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching calls:', e);
      } finally {
        this.loading = false;
        this.updateTableScrollOverflowFlag();
      }
    },
    /**
     * Açık bir çağrı detayı varsa yalnızca o detayı yeniden yükler (liste ve açık satır korunur);
     * yoksa yeni çağrıları görmek için listeyi mevcut filtrelerle ilk sayfadan yeniden yükler.
     */
    async refreshCalls() {
      if (this.loading || this.loadingMore || this.loadingDetails) return;
      if (this.expandedId !== null) {
        this.pauseAudio();
        this.revokeAllMessageAudioUrls();
        this.resetCallRecordingState();
        await this.fetchExpandedDetails(this.expandedId);
        return;
      }
      const el = this.$refs.tableScrollArea;
      if (el) el.scrollTop = 0;
      await this.fetchCalls();
    },
    async loadMoreCalls() {
      if (this.loading || this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      const nextPage = (this.pagination?.page ?? 1) + 1;
      try {
        const response = await callHistoryService.getListPage(
          this.buildActiveFiltersForApi({ includePaging: true, page: nextPage }),
        );
        if (response.success) {
          const chunk = response.items;
          const merged = { ...(this.pagination || {}), ...(response.pagination || {}) };
          if (chunk.length === 0 || chunk.length < this.pageSize) {
            merged.paginationExhausted = true;
            merged.inferredMore = false;
          }
          this.calls = [...this.calls, ...chunk];
          this.pagination = merged;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error loading more calls:', e);
      } finally {
        this.loadingMore = false;
        this.updateTableScrollOverflowFlag();
      }
    },
    triggerCsvDownload(blob, filename) {
      const fallbackDate = new Date().toISOString().replace(/[:.]/g, '-');
      const safeFilename = (filename && String(filename).trim())
        ? String(filename).trim()
        : `calls-export-${fallbackDate}.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = safeFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async exportCalls() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const { blob, filename } = await callHistoryService.exportCalls(this.buildActiveFiltersForApi());
        this.triggerCsvDownload(blob, filename);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('CallHistory exportCalls:', e);
      } finally {
        this.exporting = false;
      }
    },
    async toggleRow(id) {
      if (this.expandedId === id) {
        this.pauseAudio();
        this.revokeAllMessageAudioUrls();
        this.resetCallRecordingState();
        this.expandedId = null;
        this.expandedCallData = null;
        return;
      }
      this.pauseAudio();
      this.revokeAllMessageAudioUrls();
      this.resetCallRecordingState();
      this.expandedId = id;
      const loaded = await this.fetchExpandedDetails(id);
      if (loaded) AnalyticsService.track(EVENTS.CALL_DETAIL_VIEWED, { call_id: id });
    },
    async fetchExpandedDetails(id) {
      this.loadingDetails = true;
      try {
        const response = await callHistoryService.getCallDetails(id);
        if (this.expandedId !== id) return false;
        if (response.success && response.data) {
          this.expandedCallData = response.data;
          return true;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching details:', e);
      } finally {
        if (this.expandedId === id || this.expandedId === null) this.loadingDetails = false;
      }
      return false;
    },
    jumpToTime(time) {
      const el = this.$refs.audioEl;
      AnalyticsService.track(EVENTS.CALL_TRANSCRIPT_SEGMENT_PLAYED, { call_id: this.expandedId });
      if (el && Array.isArray(el)) {
        const audio = el[0];
        if (audio) {
          audio.currentTime = time;
          audio.play();
          this.isPlaying = true;
        }
      } else if (el) {
        el.currentTime = time;
        el.play();
        this.isPlaying = true;
      }
    },
    pauseAudio() {
      const el = this.$refs.audioEl;
      const audio = Array.isArray(el) ? el[0] : el;
      if (audio) {
        audio.pause();
        this.isPlaying = false;
      }
    },
    revokeAllMessageAudioUrls() {
      Object.values(this.messageAudioUrls).forEach((u) => {
        if (u) URL.revokeObjectURL(u);
      });
      this.messageAudioUrls = {};
      this.messageAudioLoading = {};
      this.messageAudioMissing = {};
    },
    async loadMessageAudio(messageId) {
      this.$set(this.messageAudioLoading, messageId, true);
      try {
        const blob = await callMessageService.fetchAudioBlob(messageId);
        const url = URL.createObjectURL(blob);
        this.$set(this.messageAudioUrls, messageId, url);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('CallHistory loadMessageAudio:', e);
        this.$set(this.messageAudioMissing, messageId, true);
      } finally {
        this.$set(this.messageAudioLoading, messageId, false);
      }
    },
    resetCallRecordingState() {
      if (this.callRecordingUrl) {
        URL.revokeObjectURL(this.callRecordingUrl);
        this.callRecordingUrl = null;
      }
      this.callRecordingLoading = false;
      this.callRecordingMissing = false;
    },
    async loadCallRecording() {
      const id = this.expandedId;
      if (!id) return;
      this.callRecordingLoading = true;
      this.callRecordingMissing = false;
      try {
        const blob = await callHistoryService.fetchAudioBlob(id);
        const url = URL.createObjectURL(blob);
        if (this.expandedId !== id) {
          URL.revokeObjectURL(url);
          return;
        }
        this.callRecordingUrl = url;
        AnalyticsService.track(EVENTS.CALL_AUDIO_DOWNLOADED, { call_id: id });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('CallHistory loadCallRecording:', e);
        this.callRecordingMissing = true;
      } finally {
        this.callRecordingLoading = false;
      }
    },
    toggleSegmentMetrics(idx) {
      this.$set(this.expandedSegments, idx, !this.expandedSegments[idx]);
    },
    onTimeUpdate(e) {
      this.currentTime = e.target.currentTime;
    },
    isSegmentActive(segment) {
      return (
        this.currentTime >= segment.timestamp_start
        && this.currentTime < segment.timestamp_end
      );
    },
    formatDuration(seconds) {
      if (seconds == null) return '0:00';
      const mins = Math.floor(Number(seconds) / 60);
      const secs = Math.floor(Number(seconds) % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      return new Date(dateTime).toLocaleString();
    },
    getStatusClass(status) {
      switch (String(status || '').toLowerCase()) {
      case 'completed':
      case 'ended':
        return 'completed';
      case 'failed':
      case 'canceled':
      case 'connection error':
        return 'failed';
      case 'ongoing':
      case 'live':
        return 'ongoing';
      default:
        return 'default';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";
@import "../styles/colors.scss";

.call-history-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.call-history-wrapper .content-area {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.main-content {
  /* Keep scroll on .table-area--scroll (global layout); overflow:auto here steals the scroll target and breaks infinite scroll. */
  overflow: hidden;
  padding: 24px;
  min-height: 0;
}

.page-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.refresh-btn-icon {
  margin-right: 6px;
  opacity: 0.95;
}

.refresh-btn-icon--spinning {
  animation: refresh-spin 0.8s linear infinite;
}

@keyframes refresh-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
}

.filter-btn-wrap {
  position: relative;
}

.filter-btn-icon {
  margin-right: 6px;
  opacity: 0.95;
}

.filter-active-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  background: $light-surface-2;
  border-radius: 9999px;
  padding: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px $light-border;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  padding: 16px;
  background: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: $card-border-radius;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  .stat-label {
    font-size: $caption-1-size;
    font-weight: 600;
    text-transform: uppercase;
    color: $light-foreground-4;
    margin: 0 0 8px 0;
  }
  .stat-value {
    font-size: $h4-size;
    font-weight: 700;
    color: $light-foreground-1;
    margin: 0;
  }
}

.search-input-field :deep(.v-field) {
  background-color: $light-surface-1;
  border-radius: 8px;
}

.call-filter-drawer {
  :deep(.v-navigation-drawer) {
    top: 0;
    height: 100vh;
  }
  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.filter-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid $light-border;
}

.filter-drawer-title {
  margin: 0;
  font-size: $body-2-size;
  font-weight: 700;
  color: $light-foreground-1;
}

.filter-drawer-body {
  flex: 1;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  :deep(.v-input) {
    margin-top: 0;
    padding-top: 0;
  }

  :deep(.v-input__slot) {
    margin-bottom: 0;
  }

  :deep(.v-text-field__details) {
    min-height: 0;
    margin: 0;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-field--date + .filter-field--date {
  margin-top: 10px;
}

.filter-drawer-footer {
  padding: 14px 18px;
  border-top: 1px solid $light-border;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.filter-search-hint {
  margin: 4px 0 0 0;
  font-size: $caption-1-size;
  color: $light-foreground-4;
}

.loading-state,
.empty-state-table {
  padding: 32px;
  text-align: center;
  color: $light-foreground-4;
  background: $light-surface-2;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
}

.table-container {
  background: $light-surface-2;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
  overflow: hidden;
}

.scroll-pagination-hint {
  padding: 14px 24px;
  text-align: center;
  font-size: $caption-1-size;
  color: $light-foreground-4;
  border-top: 1px solid $light-border;
  background: $light-surface-1;

  &--end {
    color: $light-foreground-5;
  }
}

.load-more-btn {
  margin: 0 auto;
  display: inline-flex;
}

.call-history-table {
  width: 100%;
  text-align: left;
  font-size: $body-3-size;
  color: $light-foreground-3;
  border-collapse: collapse;

  thead {
    background: $light-surface-1;
    color: $light-foreground-3;
    text-transform: uppercase;
    font-weight: 600;
    font-size: $caption-1-size;
    letter-spacing: 0.5px;

    th {
      padding: 12px 24px;
      border-bottom: 1px solid $light-border;
    }
  }

  tbody {
    .data-row {
      transition: background-color 0.2s;

      &:hover {
        background-color: $light-surface-1;
      }
      &.row-expanded {
        background-color: rgba($light-primary, 0.06);
      }

      td {
        padding: 16px 24px;
        border-bottom: 1px solid $light-border;
        vertical-align: middle;
      }
    }

    .font-medium {
      font-weight: 600;
      color: $light-foreground-1;
    }
    .customer-id-cell {
      font-size: $body-3-size;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      color: $light-foreground-1;
      min-width: 7.5rem;
      max-width: 12rem;
      word-break: break-all;
    }

    .customer-number-cell {
      font-size: $body-3-size;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      color: $light-foreground-1;
      min-width: 8rem;
      max-width: 12rem;
      word-break: break-all;
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: $caption-1-size;
  font-weight: 600;

  &.status-completed {
    background: $light-success-light;
    color: $light-success-medium;
  }
  &.status-failed {
    background: rgba($light-error, 0.12);
    color: $light-error-medium;
  }
  &.status-ongoing {
    background: $light-info-light;
    color: $light-info-medium;
  }
  &.status-default {
    background: $light-surface-3;
    color: $light-foreground-3;
  }
}

.detail-row .detail-cell {
  padding: 0;
  border-bottom: 1px solid $light-border;
  vertical-align: top;
  background: $light-surface-1;
}

.detail-inner {
  padding: 24px;
  border-bottom: 2px solid $light-surface-1;
  animation: fadeIn 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.detail-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
  color: $light-foreground-4;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.audio-block {
  background: $light-surface-2;
  padding: 16px;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .audio-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .call-meta-lines {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 0 12px;
  }
  .call-meta-line {
    margin: 0;
    font-size: $body-3-size;
    line-height: 1.5;
    color: $light-foreground-1;
  }
  .call-meta-label {
    display: inline-block;
    min-width: 5.5rem;
    margin-right: 8px;
    font-weight: 600;
    color: $light-foreground-3;
  }
  .call-meta-value {
    word-break: break-word;
  }
  .call-meta-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: $caption-1-size;
    color: $light-foreground-1;
  }
  .detail-body-loading {
    font-size: $body-3-size;
    color: $light-foreground-4;
    margin: 0;
    padding-top: 4px;
  }
  .audio-label {
    font-size: $caption-1-size;
    font-weight: 700;
    color: $light-foreground-4;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .sentiment-badge {
    font-size: $caption-1-size;
    font-weight: 500;
    color: $light-info;
  }
  .audio-element {
    width: 100%;
    height: 40px;
  }
}

.no-audio {
  font-size: $body-3-size;
  color: $light-foreground-4;
  font-style: italic;
  margin: 0;
}

.call-recording-load {
  margin-top: 4px;
  padding: 0 !important;
  min-width: 0 !important;
  height: auto !important;
}

.call-recording-loading {
  font-size: $caption-1-size;
  color: $light-foreground-4;
  margin-top: 6px;
  display: inline-block;
}

.metadata-block {
  background: $light-surface-2;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.metadata-expand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  background: $light-surface-1;
  border: none;
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba($light-primary, 0.04);
  }

  &:focus {
    outline: 2px solid rgba($light-primary, 0.35);
    outline-offset: -2px;
  }
}

.metadata-expand-title {
  font-size: $caption-1-size;
  font-weight: 700;
  color: $light-foreground-4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metadata-chevron {
  flex-shrink: 0;
}

.metadata-expand-body {
  padding: 16px;
  border-top: 1px solid $light-border;
  background: $light-surface-2;
}

.metadata-pre {
  margin: 0;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: $caption-1-size;
  line-height: 1.45;
  color: $light-foreground-1;
  background: $light-surface-1;
  border-radius: 8px;
  border: 1px solid $light-border;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.transcript-block {
  background: $light-surface-2;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 16px;
}

.transcript-title {
  font-size: $body-3-size;
  font-weight: 700;
  color: $light-foreground-3;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid $light-border;
}

.transcript-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 16px;
  border-left: 2px solid $light-surface-3;
  margin-left: 8px;
}

.transcript-segment {
  position: relative;
  padding-left: 24px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    opacity: 1;
  }
  &.segment-active {
    opacity: 1;
    transform: scale(1.01);
  }
  &:not(.segment-active) {
    opacity: 0.85;
  }
}

.segment-dot {
  position: absolute;
  left: -21px;
  top: 12px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid $light-surface-2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  z-index: 1;
  background: $light-foreground-5;

  &.dot-active {
    background: $light-success;
    box-shadow: 0 0 0 4px $light-success-light;
  }
  &.dot-agent {
    background: $light-info;
  }
}

.segment-bubble {
  padding: 12px;
  border-radius: $card-border-radius;
  border: 1px solid $light-border;
  background: $light-surface-2;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;

  &:hover {
    background: $light-surface-1;
  }
  &.bubble-active {
    background: $light-info-light;
    border-color: $light-info-neutral;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  }
}

.segment-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.segment-meta-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.stage-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  background: $light-surface-3;
  color: $light-foreground-3;
  letter-spacing: 0.03em;
}

.confidence-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  background: transparent;
  border: 1px solid $light-surface-3;
  color: $light-foreground-4;
  letter-spacing: 0.03em;
}

.segment-speaker {
  font-size: $caption-1-size;
  font-weight: 700;
  text-transform: uppercase;
  color: $light-foreground-3;
}
.transcript-segment.segment-agent .segment-speaker {
  color: $light-info;
}
.segment-time {
  font-size: $caption-1-size;
  font-family: monospace;
  color: $light-foreground-4;
}
.segment-text {
  font-size: $body-3-size;
  color: $light-foreground-1;
  line-height: 1.5;
  margin: 0;
}

.segment-metrics {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid $light-border;
}

.segment-metrics-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: $caption-1-size;
  color: $light-foreground-4;
  transition: color 0.15s;

  &:hover {
    color: $light-foreground-2;
  }
}

.segment-metrics-body {
  margin-top: 10px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px 16px;
}

.metric-item {
  .metric-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: $light-foreground-4;
    letter-spacing: 0.05em;
    margin: 0 0 2px;
  }

  .metric-value {
    font-size: $caption-1-size;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: $light-foreground-1;
    margin: 0;
  }
}

.segment-tool-block {
  margin-top: 10px;

  .metric-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: $light-foreground-4;
    letter-spacing: 0.05em;
    margin: 0 0 4px;
  }

  .metadata-pre {
    padding: 8px;
  }
}

.message-audio-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid $light-border;
}

.message-audio-el {
  width: 100%;
  height: 36px;
  margin-top: 4px;
}

.message-audio-load {
  margin-top: 4px;
  padding: 0 !important;
  min-width: 0 !important;
  height: auto !important;
}

.message-audio-loading,
.message-audio-none {
  font-size: $caption-1-size;
  color: $light-foreground-4;
  margin-top: 6px;
}

.message-audio-none {
  font-style: italic;
}
</style>
